<template>
  <div
    ref="listingPage"
    class="listing-page"
  >
    <ExcursionFilterSection
      :currencyCode
      :slug
      :page
      :geoObject
      :defaultSoguFields
      @filters-updated="onFiltersUpdated"
      @tagChanged="currentTag = $event"
    />

    <ExcursionExpSelectionBlock
      v-if="isTopSelectionVisible || isCityTagsCollectionsExperimentActive"
      :slug="slug"
      :location="location"
      :context-geo-subject="defaultSoguFields"
      :page="page"
      :is-extra-selection="false"
      :position="'top'"
      :is-city-tags-collections-experiment-active="isCityTagsCollectionsExperimentActive"
    />

    <ExcursionAllCardsBlock
      :location="location"
      :slug="slug"
      :page="page"
      :default-sogu-fields="defaultSoguFields"
      :has-injection="currentTag?.value !== 'all'"
      @pageSelected="handleNewPage"
      @excludedIdsUpdated="excludedIds = $event"
    />

    <ExcursionExpSelectionBlock
      v-if="!isCityTagsCollectionsExperimentActive"
      :slug="slug"
      :location="location"
      :context-geo-subject="defaultSoguFields"
      :page="page"
      :is-extra-selection="true"
      :position="'bottom'"
    />
  </div>

  <section
    v-if="shouldShowPopularSection"
    class="wrap"
  >
    <h2 class="popular-title">
      {{ t('page.popularTitle', { cityName5Ru: cityInfo?.name_5_ru }) }}
    </h2>
    <ExcursionCardList
      class="popular-list"
      :exp-list="experiencesPopularWithMovementType"
      :context-geo-subject="defaultSoguFields"
      :show-type="false"
      :location="location"
    />
  </section>

  <section
    v-if="shouldShowRecommendationSection"
    class="wrap"
  >
    <h2
      v-if="recommendationIsShown"
      class="recommendations-title"
    >
      {{ t('page.recommendationTitle') }}
    </h2>
    <ExcursionCardList
      class="recommendations-list"
      sogu-block="section"
      :recommendation-listing-sogu-params="recommendationListingSoguParams"
      :exp-list="experienceRecommendation"
      :context-geo-subject="defaultSoguFields"
      :show-type="false"
    />
  </section>

  <AttractionsBlock
    :sogu-url="VITE_RUNTIME_SOGU_SERVICE_URL"
    :api-url="apiProxyUrl"
    :slug="slug"
    :tag="currentTagRaw"
    :default-sogu-fields="defaultSoguFields"
    :geo-object="geoObject"
    :sights-url="sightsUrl"
    :experiment-data="experimentData"
  />

  <CityToursBlock
    :slug="slug"
    :geo-object="geoObject"
    :default-sogu-fields="defaultSoguFields"
  />

  <CityAroundList
    v-if="cityInfo"
    :city-info="cityInfo"
    :default-sogu-fields="defaultSoguFields"
    :city-slug="slug"
  />

  <TravelersReviewsBlock
    v-if="isTravelersReviewsBlockShown"
    :slug="slug"
    :default-sogu-fields="defaultSoguFields"
  />

  <ExcursionGuidesBlock
    :slug="slug"
    :location="location"
    :default-sogu-fields="defaultSoguFields"
    :geo-object="geoObject"
  />

  <ArticlesBlock
    :slug="slug"
    :location="location"
    :geo-object="geoObject"
    :default-sogu-fields="defaultSoguFields"
  />

  <SubscriptionBlock
    v-if="!loaderStore.isFiltersLoading"
    class="wrap"
    :api-url="VITE_RUNTIME_API_URL"
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
  />

  <ExcursionSeoExperienceWrapper
    :slug="slug"
    :default-sogu-fields="defaultSoguFields"
    :page="page"
  />
</template>

<script lang="ts" setup>
  import { useRoute } from 'vue-router';
  import { computed, inject, ref, type useShadowRoot, watch } from 'vue';

  // Widgets
  import { ExcursionSeoExperienceWrapper } from '@/widgets/seoBlock/excursion';
  import { ExcursionGuidesBlock } from '@/widgets/guidesBlock/excursion';
  import { ExcursionAllCardsBlock } from '@/widgets/allCardsBlock/excursion';

  import { ExcursionExpSelectionBlock } from '@/widgets/expSelectionsBlock/excursion';
  import ExcursionCardList from '@/widgets/allCardsBlock/ui/excursion/ExcursionCardList.vue';

  import ArticlesBlock from '@/widgets/articlesBlock/ui/ArticlesBlock.vue';
  import { TravelersReviewsBlock } from '@/widgets/travelersReviewsBlock';
  import { CityToursBlock } from '@/widgets/cityToursBlock';

  // Features
  import { useExcursionPageViewTracker, useExcursionSoguStore } from '@/features/sogu/excursion';
  import { useExcursionSmallOutput } from '@/features/smallOutput/excursion';
  import { useWidgetsShareStore } from '@/features/widgets-share';
  import { useCityCollectionsExperiment, useCityTagsCollectionsExperiment } from '@/features/experiments';

  // Entities
  import { useCityInfo } from '@/entities/city/api/useCityInfo.ts';
  import { CityAroundList } from '@/entities/city';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { useGetExperiencesRecommendations } from '@/entities/experience/recommendation';
  import { useGetExperiencesPopular } from '@/entities/experience/popular';
  import { useExperienceWithMovementType, useMovementTypes } from '@/entities/movement-type';
  import type { ExtendedPartTagType } from '@/entities/rubrics';
  import { excursionFiltersToQuery, useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import { useUserStore } from '@/entities/user';

  // Shared and external imports
  import { type ContextGeoSubject, GEO_TYPES } from '@/shared/types/geography';
  import { filterScroll, paginationScroll } from '@/shared/lib/use-scroll';
  import type { CityInfo } from '@/shared/api/experience';
  import { type CurrencyCode, toCurrencyCode } from '@/shared/lib/currency';
  import { useExcursionScrollRestoration } from '@/shared/composables/excursion/useExcursionScrollRestoration';
  import { useListingRouteParams } from '../../composables/useListingRouteParams.ts';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import ExcursionFilterSection from './ExcursionFiltersSection.vue';
  import { reportMFRendered } from '@experience/core-hostapp-share-lib';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useI18n } from '@experience/i18n';
  import ru from '../../../i18n/ru.json';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { useRefHistory } from '@vueuse/core';
  import { AttractionsBlock } from '@experience/attractions-for-listing-package';
  import { sendMindboxEvent } from '@experience/core-consent-package';
  import { useScreenSize } from '@experience/shared';
  import { buildExcursionMindboxSearchPayload } from '@/pages/listing/lib/excursion/ExcursionMindboxSearchPayload';
  import { useMindboxSearchEvent, useMindboxSearchLabelMaps } from '@/pages/listing/composables';

  type MessageSchema = typeof ru;

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const { VITE_RUNTIME_API_URL, VITE_RUNTIME_SOGU_SERVICE_URL, VITE_RUNTIME_IS_DEV } = useRuntimeConfig<false>();

  const apiProxyUrl = computed(() => (VITE_RUNTIME_IS_DEV ? '/monolith_api' : window.location.origin));

  const route = useRoute();
  const router = useRouter();

  const MF_NAME = 'experience-mf-listing';

  useExcursionScrollRestoration();

  const mindBoxDefaultData = computed(() => {
    const ids = {} as {
      cityProducts?: number;
    };

    ids.cityProducts = geoObject.value.id ?? 0;

    return {
      data: {
        viewProduct: {
          product: {
            ids,
          },
        },
      },
      operation: 'ViewZone',
    };
  });

  const { loadFavorites } = useExperienceFavorites();
  const { showSmallOutput: recommendationIsShown } = useExcursionSmallOutput();
  const excludedIds = ref([]);

  const excludedIdsParams = computed(() => excludedIds.value.join(',') || '');

  const userStore = useUserStore();
  userStore.getUser();

  // прокидываем router в store
  const filterStore = useExcursionFiltersStore();
  filterStore.initRouter(router);

  const loaderStore = useLoaderStore();

  const widgetsShareStore = useWidgetsShareStore();

  const { slug, location, isLocation } = useListingRouteParams();

  const isTravelersReviewsBlockShown = computed(
    () => isLocation.value.isCity && !widgetsShareStore.isAllExperiencesPaused && !loaderStore.isFiltersLoading,
  );

  const { isCollectionsHidden } = useCityCollectionsExperiment(isLocation);

  const isTopSelectionVisible = computed(() => !isLocation.value.isCountry && !isCollectionsHidden.value);

  const currentTag = ref<ExtendedPartTagType | null>(null);

  const currentTagRaw = computed(() => {
    return (route.params.tag as string | undefined) || currentTag.value?.value;
  });

  const { cityInfo } = useCityInfo(
    slug,
    computed(() => true),
  );

  const geoObject = computed(() => cityInfo.value || ({} as CityInfo));

  const soguStore = useExcursionSoguStore();
  const { movementTypesExpCardLabels } = useMovementTypes();
  const { formatLabels, durationLabels, startTimeLabels, sortingLabels } = useMindboxSearchLabelMaps();

  useMindboxSearchEvent(() => {
    const cityName = geoObject.value?.name_ru ?? '';

    if (!cityName) {
      return null;
    }

    return buildExcursionMindboxSearchPayload({
      searchState: filterStore.searchState,
      pageParams: filterStore.pageParams,
      cityName,
      movementTypeLabels: (movementTypesExpCardLabels.value ?? undefined) as
        | Record<string, string | undefined>
        | null
        | undefined,
      formatLabels: formatLabels.value ?? undefined,
      durationLabels: durationLabels.value ?? undefined,
      startTimeLabels: startTimeLabels.value ?? undefined,
      sortingLabels: sortingLabels.value ?? undefined,
    });
  });

  watch(
    () => geoObject.value,
    (value) => {
      soguStore.setGeoObject(value);
      if (value.id) {
        sendMindboxEvent({
          ...mindBoxDefaultData.value,
        });
      }
    },
    { deep: true, immediate: true },
  );

  const requestParams = computed(() => {
    const isBookable = filterStore.isFiltersApplied ? 'true' : 'all';

    return {
      ...excursionFiltersToQuery(filterStore.searchState, filterStore.pageParams),
      bookable: isBookable,
      price_format: 'travelers' as const,
      exclude_ids: excludedIdsParams.value,
    };
  });

  const requestParamsPopular = computed(() => {
    const isBookable = filterStore.isFiltersApplied ? 'true' : 'all';

    return {
      ...excursionFiltersToQuery(filterStore.searchState, filterStore.pageParams),
      bookable: isBookable,
      price_format: 'travelers' as const,
      city: cityInfo.value?.id,
    };
  });

  const hasZeroRubricsOutput = computed(() => !currentTag.value?.counter);

  const { experiencesData, recommendationAlgorithmMeta } = useGetExperiencesRecommendations(
    requestParams,
    recommendationIsShown,
  );

  const recommendationListingSoguParams = computed(() => ({
    section_name: t('page.recommendationTitle'),
    block_type: 'recommendations' as const,
    ...recommendationAlgorithmMeta.value,
  }));

  const { experiencesData: experiencesPopular } = useGetExperiencesPopular(requestParamsPopular, hasZeroRubricsOutput);

  const experienceRecommendation = useExperienceWithMovementType({
    experiences: computed(() => {
      if (Array.isArray(experiencesData)) {
        return experiencesData;
      }

      return experiencesData?.value?.results ?? [];
    }),
  });

  const experiencesPopularWithMovementType = useExperienceWithMovementType({
    experiences: computed(() => {
      if (Array.isArray(experiencesPopular)) {
        return experiencesPopular;
      }

      return experiencesPopular?.value?.results ?? [];
    }),
  });

  const currencyCode = computed<CurrencyCode>(() => {
    const geo = geoObject.value;
    let currencyValue: string | undefined;

    if ('country' in geo && geo.country && 'currency' in geo.country && geo.country.currency) {
      currencyValue = geo.country.currency;
    } else if (
      'countries' in geo &&
      Array.isArray(geo.countries) &&
      geo.countries.length > 0 &&
      geo.countries[0].currency
    ) {
      currencyValue = geo.countries[0].currency;
    }

    return toCurrencyCode(currencyValue);
  });

  // Текущая страница
  const page = computed(() => {
    return Number(route.query?.page ?? 1);
  });

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const handleNewPage = (newPage: number, isScroll = false) => {
    const params = structuredClone(route.query);

    if (newPage === 1) {
      // Чтобы не записывалось в url page на первой странице
      delete params.page;
    } else {
      params.page = newPage.toString();
    }

    router.push({
      query: {
        ...params,
      },
    });
    if (isScroll) {
      // Не успевает пеерсчитать высоту блока, нужен timeout
      paginationScroll(rootNode);
      handleListingPageViewEvent();
    }
  };

  const sightsUrl = computed(() => {
    const { citySlug } = route.params;

    return makeFullLink(`/attractions/${citySlug}/`);
  });

  const defaultSoguFields = computed<ContextGeoSubject>(() => {
    return {
      soguStamp: {
        subject_geo_id: geoObject.value.id ?? 0,
        subject_geo_name: (geoObject.value as CityInfo)?.name_ru || '',
        subject_geo_type: GEO_TYPES.CITY,
      },
    };
  });

  const { trackListingPageViewEvent } = useExcursionPageViewTracker();

  const listingPage = ref<HTMLElement | null>(null);

  const handleListingPageViewEvent = () => {
    let geo: CityInfo | null = null;

    if (cityInfo.value) {
      geo = cityInfo.value;
    }

    if (geo) {
      trackListingPageViewEvent(soguStore.rubricId, geo, defaultSoguFields.value);
    }
  };

  const unwatchLoader = watch(
    () => loaderStore.isFiltersLoading,
    (value) => {
      if (!value) {
        handleListingPageViewEvent();

        queueMicrotask(() => {
          unwatchLoader();
        });
      }
    },
    { immediate: true },
  );

  // Задаем параметры с query для store
  watch(
    () => [route.query, route.params],
    ([query, params], prevValues) => {
      filterStore.setInitialStore(query, (params?.tag as string) || '');

      // Сброс флагов загрузки при изменении параметров роута
      if (prevValues) {
        const [, prevParams] = prevValues;
        if (prevParams && JSON.stringify(params) !== JSON.stringify(prevParams)) {
          const prevParamsObj = prevParams as Record<string, unknown>;
          const currentParamsObj = params as Record<string, unknown>;

          // Проверка изменения локации (citySlug)
          const locationKeys = ['citySlug'];
          const locationChanged = locationKeys.some((key) => prevParamsObj[key] !== currentParamsObj[key]);

          // Reset flags based on what changed
          if (locationChanged) {
            loaderStore.ready.seo = false;
            loaderStore.ready.rubrics = false;
          }
          // Tag change is handled by CoverBlock watcher
        }
      }
    },
    { deep: true, immediate: true },
  );

  const observer = ref<IntersectionObserver | null>(null);

  onBeforeUnmount(() => {
    observer.value?.disconnect();
  });

  onMounted(() => {
    reportMFRendered(MF_NAME);
    if (geoObject.value.id) {
      sendMindboxEvent({ ...mindBoxDefaultData.value });
    }
  });

  loadFavorites();

  const { history } = useRefHistory(
    computed(() => filterStore.state),
    { deep: true },
  );

  const { isExpActive } = useCityTagsCollectionsExperiment();
  const isCityTagsCollectionsExperimentActive = computed(() => isExpActive.value);
  const experimentData = computed(() => {
    if (!isCityTagsCollectionsExperimentActive.value) {
      return {
        city__slug: slug.value,
        page_size: 10,
        sorting: 'ranking_mab',
        tag__is_attraction: true,
      } as { city__slug: string; page_size: number; sorting: 'ranking_mab' };
    }

    return undefined;
  });

  const { isMobile } = useScreenSize();

  function onFiltersUpdated() {
    if (loaderStore.isFiltersLoading) {
      return;
    }

    const last = history.value[2];
    const currentFilters = toRaw(filterStore.state);

    const changedKeys: string[] = [];

    Object.keys(currentFilters).forEach((key) => {
      const lastValue = JSON.stringify(last?.snapshot[key as keyof (typeof last)['snapshot']]);
      const currentValue = JSON.stringify(currentFilters[key as keyof typeof currentFilters]);

      if (lastValue !== currentValue) {
        changedKeys.push(key);
      }
    });

    const routeQueryKeys = Object.keys(route.query);

    // Проверка на случай, если фильтры были сброшены до по умолчанию при изменении сортировки на дефолтную
    const isOnlySortingReset = changedKeys.length === 1 && changedKeys[0] === 'sorting';

    const isReset = routeQueryKeys.length === 0 && !isOnlySortingReset;

    const isPageQuery = routeQueryKeys.includes('page');

    if (!isPageQuery && !isMobile.value) {
      setTimeout(() => {
        filterScroll(rootNode, isReset, isCityTagsCollectionsExperimentActive.value);
      });
    }
    handleListingPageViewEvent();
  }

  const shouldShowPopularSection = computed(() => {
    return (
      hasZeroRubricsOutput.value && experiencesPopularWithMovementType.value.length > 0 && !recommendationIsShown.value
    );
  });

  const shouldShowRecommendationSection = computed(() => {
    return experienceRecommendation.value.length > 0 && recommendationIsShown.value;
  });

  const verticalsMenuSelect = () => {
    /**
     * отправляем событие для подсвечивания пункта меню в "вертикалях"
     */
    document.dispatchEvent(
      new CustomEvent('updateVerticalsMenuActive', {
        detail: {
          active: 'experiences',
        },
      }),
    );
  };

  watch(
    () => route.path,
    () => {
      if (geoObject.value.id) {
        sendMindboxEvent({ ...mindBoxDefaultData.value });
      }

      verticalsMenuSelect();
    },
  );

  onMounted(() => {
    verticalsMenuSelect();
  });
</script>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  // TODO
  // Временное решение для скрытия второго скролла в модалке
  // Если все хорошо - нужно перенести в uikit
  /* stylelint-disable-next-line order/order */
  .ui-modal__scroll-container::-webkit-scrollbar {
    display: none !important;
  }

  // И это тоже. В фильтрах чекбоксы времени начала перекрывают z-index 2
  .ui-modal-header_fixed {
    z-index: 10 !important;
  }

  .listing-page {
    padding-top: 40px;
    scrollbar-gutter: stable;

    @include adaptivity.mobile {
      padding-top: 24px;
    }
  }

  .wrap {
    box-sizing: border-box;
    min-width: 950px;
    max-width: 1260px;
    margin: 0 auto;
    padding: 0 20px;

    @include adaptivity.tablet {
      width: 100%;
      min-width: 100%;
      max-width: 100vw;
      padding: 0 20px;
    }
  }

  .azure-link {
    color: var(--brand-color);
    text-decoration: none;
    cursor: pointer;
  }

  .azure-dashed {
    border-bottom: 1px dashed var(--brand-color);
  }

  .blue-link {
    color: #2e78c1;
    text-decoration: none;
    cursor: pointer;
  }

  .recommendations-title,
  .popular-title {
    margin-top: 56px;

    @include typography.base-700-500-extra;

    @include adaptivity.mf-desktop {
      margin-top: 80px;

      @include typography.base-800-750-extra;
    }
  }

  .recommendations-list,
  .popular-list {
    margin-top: 16px;

    @include adaptivity.mf-desktop {
      margin-top: 24px;
    }
  }

  .listing-page .ui-chip,
  .listing-page .ui-chip__label-container {
    max-width: inherit;
  }

  .sights-wrap {
    margin-top: 80px;

    @include adaptivity.tablet {
      margin-top: 56px;
    }
  }

  h1,
  h2,
  h3,
  figure {
    margin: 0;
  }

  :host {
    margin: 0;
    scrollbar-gutter: stable;
  }
</style>
